import axiosClient from '@/core/api/axiosClient';
import { ADMINISTRACION_ENDPOINTS } from './endpoints';
import { getDeviceId } from '@/core/utils/deviceId';
import { getDeviceInfo } from '@/core/utils/deviceInfo'; // ✅ Feature Metadata para auditoría

export interface LoginRequest {
  UsuarioId: string;
  Contrasena: string;
  DispositivoId?: string; // Feature Multi-sesión
  // ✅ Feature Metadata para auditoría
  Navegador?: string;
  SistemaOperativo?: string;
  TipoDispositivo?: string;
  Modelo?: string;
}

export interface LoginResponse {
  data: {
    token: string;
    usuario: {
      id: bigint;
      usuarioId: string;
      nombre: string;
      correo: string;
      roles: string[];
      cuentaActiva: boolean;
    };
    tokenExpiracion: string;
    requiereCambioContrasena: boolean;
  };
  message: string;
  success: boolean;
  errors: string[];
}

export interface ApiResponse<T> {
  data?: T;
  message?: string;
  success: boolean;
  errors?: string[];
}

export interface RefreshTokenResponse {
  data: {
    token: string;
    usuario: {
      id: bigint;
      usuarioId: string;
      nombre: string;
      correo: string;
      roles: string[];
      cuentaActiva: boolean;
    };
    tokenExpiracion: string;
  };
  message: string;
  success: boolean;
  errors: string[];
}

export const authClient = {
  /**
   * Login de usuario
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      // ✅ Feature 5: Obtener metadata del dispositivo para auditoría
      const deviceInfo = getDeviceInfo();

      const response = await axiosClient.post<LoginResponse>(
        ADMINISTRACION_ENDPOINTS.AUTH.LOGIN,
        {
          UsuarioId: credentials.UsuarioId,
          Contrasena: credentials.Contrasena,
          DispositivoId: credentials.DispositivoId, // Feature 4: Multi-sesión
          // ✅ Feature 5: Metadata para auditoría de sesiones
          Navegador: deviceInfo.navegador,
          SistemaOperativo: deviceInfo.sistemaOperativo,
          TipoDispositivo: deviceInfo.tipoDispositivo,
          Modelo: deviceInfo.modelo,
        }
      );
      return response.data;
    } catch (error: any) {
      // Parsear errores FluentValidation (.NET ProblemDetails)
      if (error.response?.data?.errors) {
        const errorsObj = error.response.data.errors;
        const firstField = Object.keys(errorsObj)[0];
        if (firstField && Array.isArray(errorsObj[firstField]) && errorsObj[firstField].length > 0) {
          throw new Error(errorsObj[firstField][0]);
        }
      }

      // Formato middleware o DomainExceptions
      const errorMessage = error.response?.data?.error?.message
        || error.response?.data?.message
        || error.message
        || 'Error al iniciar sesión';

      throw new Error(errorMessage);
    }
  },

  /**
   * Logout - cerrar sesión del usuario
   */
  logout: async (): Promise<ApiResponse<void>> => {
    // Feature 4: Enviar dispositivoId para eliminar sesión de BD
    const dispositivoId = getDeviceId();
    // ✅ Feature 5: Obtener metadata del dispositivo
    const deviceInfo = getDeviceInfo();

    const response = await axiosClient.post<ApiResponse<void>>(
      ADMINISTRACION_ENDPOINTS.AUTH.LOGOUT,
      {
        UsuarioId: 0, // El backend lo sobrescribe con el del token (debe ser long, no string)
        DispositivoId: dispositivoId,
        // ✅ Feature 5: Metadata para auditoría
        Navegador: deviceInfo.navegador,
        SistemaOperativo: deviceInfo.sistemaOperativo,
        TipoDispositivo: deviceInfo.tipoDispositivo,
        Modelo: deviceInfo.modelo,
      }
    );
    return response.data;
  },

  getProfile: async (): Promise<ApiResponse<any>> => {
    const response = await axiosClient.get<ApiResponse<any>>(ADMINISTRACION_ENDPOINTS.AUTH.PROFILE);
    return response.data;
  },

  solicitarRecuperacion: async (identificador: string): Promise<ApiResponse<any>> => {
    const response = await axiosClient.post<ApiResponse<any>>(
      ADMINISTRACION_ENDPOINTS.AUTH.SOLICITAR_RECUPERACION,
      { Identificador: identificador }
    );
    return response.data;
  },

  restablecerContrasena: async (
    token: string,
    nuevaContrasena: string,
    confirmarContrasena: string
  ): Promise<ApiResponse<any>> => {
    const response = await axiosClient.post<ApiResponse<any>>(
      ADMINISTRACION_ENDPOINTS.AUTH.RESTABLECER_CONTRASENA,
      {
        Token: token,
        NuevaContrasena: nuevaContrasena,
        ConfirmarContrasena: confirmarContrasena
      }
    );
    return response.data;
  },

  // El token va en el cuerpo, nunca en la URL: la URL queda en los logs del servidor (LOG-5).
  validarToken: async (token: string): Promise<boolean> => {
    const response = await axiosClient.post<ApiResponse<boolean>>(
      ADMINISTRACION_ENDPOINTS.AUTH.VALIDAR_TOKEN,
      { Token: token }
    );
    return response.data.data || false;
  },

  /**
   * Activa una cuenta de usuario con código de activación
   */
  activarCuenta: async (codigo: string): Promise<ApiResponse<any>> => {
    // El código va en el cuerpo, nunca en la URL (LOG-5).
    const response = await axiosClient.post<ApiResponse<any>>(
      ADMINISTRACION_ENDPOINTS.AUTH.ACTIVAR_CUENTA,
      { CodigoActivacion: codigo }
    );
    return response.data;
  },

  /**
   * Renueva el access token sin solicitar credenciales
   * Requiere token actual válido (aunque esté próximo a expirar)
   * Retorna nuevo token con 1 hora adicional de validez
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    const response = await axiosClient.post<RefreshTokenResponse>(
      ADMINISTRACION_ENDPOINTS.AUTH.REFRESH_TOKEN
    );
    return response.data;
  },

  /**
   * Valida contraseña de usuario (para bloqueo de app)
   * Backend endpoint: POST /Administracion/Autenticacion/validar-contrasena
   */
  validatePassword: async (params: { usuarioId: string; contrasena: string }): Promise<boolean> => {
    try {
      const response = await axiosClient.post<ApiResponse<boolean>>(
        ADMINISTRACION_ENDPOINTS.AUTH.VALIDATE_PASSWORD,
        {
          UsuarioId: params.usuarioId,
          Contrasena: params.contrasena,
        }
      );
      return response.data.data ?? false;
    } catch (error: any) {
      // Parsear mensaje de error soportando múltiples formatos:

      // 1. Formato .NET ProblemDetails (FluentValidation auto-validation)
      // { errors: { contrasena: ["mensaje"] }, status: 400 }
      if (error.response?.data?.errors) {
        const errorsObj = error.response.data.errors;
        // Obtener primer mensaje del primer campo con errores
        const firstField = Object.keys(errorsObj)[0];
        if (firstField && Array.isArray(errorsObj[firstField]) && errorsObj[firstField].length > 0) {
          throw new Error(errorsObj[firstField][0]);
        }
      }

      // 2. Formato ExceptionHandlingMiddleware (ValidationBehavior)
      // { error: { code, message, statusCode } }
      const middlewareMessage = error.response?.data?.error?.message;
      if (middlewareMessage) {
        throw new Error(middlewareMessage);
      }

      // 3. Otros formatos (DomainExceptions, etc)
      const fallbackMessage = error.response?.data?.message
        || error.message
        || 'Error al validar contraseña';

      throw new Error(fallbackMessage);
    }
  },
};